import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
    Search,
    Settings,
    Image,
    Filter,
    Crop,
    Palette,
    Layers,
    Zap,
} from 'lucide-react';
import { Processor } from '../Processing/components/hooks/useProcessing';
import { setDragState } from './utils/state/processingFlowSlice';

interface ProcessorPaletteProps {
    processors: Processor[];
    width?: number;
}

// Icon mapping for different processor types
const getProcessorIcon = (processor: Processor) => {
    const name = processor.name.toLowerCase();
    if (name.includes('resize') || name.includes('crop')) return Crop;
    if (name.includes('filter') || name.includes('blur')) return Filter;
    if (name.includes('overlay') || name.includes('composite')) return Layers;
    if (name.includes('enhance') || name.includes('sharpen')) return Zap;
    if (name.includes('color') || name.includes('palette')) return Palette;
    return Image; // Default icon
};

// Category mapping for processors
const getProcessorCategory = (processor: Processor): string => {
    const name = processor.name.toLowerCase();
    if (
        name.includes('resize') ||
        name.includes('crop') ||
        name.includes('scale')
    )
        return 'Transform';
    if (
        name.includes('filter') ||
        name.includes('blur') ||
        name.includes('sharpen')
    )
        return 'Filters';
    if (
        name.includes('overlay') ||
        name.includes('composite') ||
        name.includes('blend')
    )
        return 'Composite';
    if (
        name.includes('color') ||
        name.includes('brightness') ||
        name.includes('contrast')
    )
        return 'Color';
    if (name.includes('text') || name.includes('watermark')) return 'Text';
    return 'General';
};

export default function ProcessorPalette({
    processors,
    width = 280,
}: ProcessorPaletteProps) {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');

    // Filter processors for local environment only
    const localProcessors = useMemo(() => {
        return processors.filter(
            (processor) =>
                !processor.environments ||
                processor.environments.includes('local') ||
                processor.environments.length === 0,
        );
    }, [processors]);

    // Group processors by category
    const categorizedProcessors = useMemo(() => {
        const categories: { [key: string]: Processor[] } = {};

        localProcessors.forEach((processor) => {
            const category = getProcessorCategory(processor);
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(processor);
        });

        return categories;
    }, [localProcessors]);

    // Get all categories
    const categories = useMemo(() => {
        return ['All', ...Object.keys(categorizedProcessors).sort()];
    }, [categorizedProcessors]);

    // Filter processors based on search and category
    const filteredProcessors = useMemo(() => {
        let filtered = localProcessors;

        // Filter by category
        if (selectedCategory !== 'All') {
            filtered = categorizedProcessors[selectedCategory] || [];
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(
                (processor) =>
                    processor.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    (processor.title &&
                        processor.title
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())) ||
                    (processor.description &&
                        processor.description
                            .toLowerCase()
                            .includes(searchTerm.toLowerCase())),
            );
        }

        return filtered;
    }, [localProcessors, categorizedProcessors, selectedCategory, searchTerm]);

    const handleDragStart = (e: React.DragEvent, processor: Processor) => {
        e.dataTransfer.setData('application/json', JSON.stringify(processor));
        e.dataTransfer.effectAllowed = 'copy';

        dispatch(
            setDragState({
                isDragging: true,
                draggedNodeType: processor.name,
            }),
        );
    };

    const handleDragEnd = () => {
        dispatch(
            setDragState({
                isDragging: false,
                draggedNodeType: undefined,
                dragHoverPosition: undefined,
            }),
        );
    };

    return (
        <div
            className="bg-background border-r border-border flex flex-col h-full"
            style={{ width }}
        >
            {/* Header */}
            <div className="p-4 border-b border-border bg-muted/50">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                        <Settings className="w-5 h-5 text-muted-foreground" />
                        <h3 className="text-lg font-semibold text-foreground">
                            Processors
                        </h3>
                    </div>
                    <span className="text-xs text-muted-foreground">
                        {filteredProcessors.length} available
                    </span>
                </div>

                {/* Search */}
                <div className="flex items-center gap-2 mb-3 rounded-md border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                    <Search className="size-4 shrink-0 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder="Search processors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                </div>

                {/* Category Filter */}
                <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                    {categories.map((category) => (
                        <option key={category} value={category}>
                            {category}{' '}
                            {category !== 'All' &&
                                `(${
                                    categorizedProcessors[category]?.length || 0
                                })`}
                        </option>
                    ))}
                </select>
            </div>

            {/* Processor List */}
            <div 
                className="p-4 space-y-2" 
                style={{ 
                    height: 'calc(100vh - 300px)', // Fixed height to enable scrolling
                    overflowY: 'auto'
                }}
            >
                {filteredProcessors.length === 0 ? (
                    <div className="text-center text-muted-foreground py-8">
                        <Settings className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                        <p>No processors found</p>
                        {searchTerm && (
                            <p className="text-sm">Try adjusting your search</p>
                        )}
                    </div>
                ) : (
                    filteredProcessors.map((processor) => {
                        const IconComponent = getProcessorIcon(processor);
                        return (
                            <div
                                key={processor.name}
                                draggable
                                onDragStart={(e) =>
                                    handleDragStart(e, processor)
                                }
                                onDragEnd={handleDragEnd}
                                className="flex items-center p-3 bg-muted/50 rounded-lg border border-border hover:bg-muted hover:border-border cursor-grab active:cursor-grabbing transition-colors group"
                                title={processor.description || processor.name}
                            >
                                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3 group-hover:bg-blue-200 transition-colors">
                                    <IconComponent className="w-5 h-5 text-blue-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-medium text-foreground truncate">
                                        {processor.title || processor.name}
                                    </h4>
                                    {processor.description && (
                                        <p className="text-xs text-muted-foreground truncate mt-1">
                                            {processor.description}
                                        </p>
                                    )}
                                    <div className="flex items-center mt-1 space-x-2">
                                        <span className="text-xs text-muted-foreground">
                                            {getProcessorCategory(processor)}
                                        </span>
                                        {processor.saveOutput && (
                                            <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded">
                                                Saves Output
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-border bg-muted/50 rounded-b-lg">
                <p className="text-xs text-muted-foreground text-center">
                    Drag processors onto the canvas to build your flow
                </p>
                <p className="text-xs text-muted-foreground text-center mt-1">
                    {filteredProcessors.length} of {localProcessors.length}{' '}
                    processors shown
                </p>
            </div>
        </div>
    );
}
